import {
  TEAMS,
  activeTeams,
  inactiveTeams,
  supportableTeams,
  teamDisplayName,
  type TeamRecord,
} from './teams'

/**
 * Thin views over the canonical team registry. This module used to BE the team
 * list, with a "never rename it" warning on `name` because the name was the de-facto primary key.
 * It isn't any more — `src/teams.ts` owns identity, `id` is the key, and renaming a club is now a
 * one-line display change. These exports stay because a lot of UI reads them, not because they
 * hold anything of their own.
 */
export type Team = TeamRecord

/**
 * Clubs **competing this season**. Reach for this whenever the meaning is "plays games":
 * fixtures, the quest team/match pickers, the PL table. Anything supportable-but-not-competing is
 * excluded on purpose.
 */
export const plTeams: readonly Team[] = activeTeams

/**
 * Clubs that are **supportable but not competing** — no fixtures, no PL table row, no quest picks.
 * They exist so their supporters can still set them as a favourite team and get a club supporter
 * league.
 */
export const legacyTeams: readonly Team[] = inactiveTeams

/**
 * Every club a user can **support**: competing clubs plus the legacy ones, ordered alphabetically
 * by display name so the relegated clubs slot into the picker in place rather than being appended
 * at the bottom. This is the list for the favourite-team picker, the club supporter league seed and
 * `clubLeagueIdForTeam` — anything that means "plays games" wants `plTeams` instead.
 */
export const favouriteTeams: readonly Team[] = supportableTeams

/** All 22 rows, competing or not, in registry order. */
export const allTeams: readonly Team[] = TEAMS

/** What we call a club in UI copy — the short form where one exists, the canonical name otherwise. */
export function clubDisplayName(team: { id?: string; name: string; shortName?: string }): string {
  return team.id ? teamDisplayName(team.id) : (team.shortName ?? team.name)
}
